import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';

import { getUid, paramCase, propsFactory, wrapInArray, warn } from '@components/utils';

import { useForm } from './form';
import { useProxyModel } from './proxyModel';
import { useToggleScope } from './toggleScope';

import type { MaybeRef } from '@components/utils';
import type { PropType, ComputedRef, Ref } from 'vue';

export type TValidationResult = string | boolean;
export type TValidationRule =
  | TValidationResult
  | PromiseLike<TValidationResult>
  | ((value: any) => TValidationResult)
  | ((value: any) => PromiseLike<TValidationResult>);
export type TValidationOn = typeof allowedValidationOn[number];

export interface IValidationProps {
  disabled: boolean;
  error: boolean;
  errorMessages: string | string[];
  focused: boolean;
  maxErrors: string | number;
  name: string | undefined;
  label: string | undefined;
  readonly: boolean;
  rules: TValidationRule[];
  modelValue: any;
  validateOn?: TValidationOn;
  validationValue: any;
  hideMessages: boolean;
}
export interface IUseValidation {
  errorMessages: ComputedRef<string[]>;
  isDisabled: ComputedRef<boolean>;
  isReadonly: ComputedRef<boolean>;
  isDirty: ComputedRef<boolean>;
  isPure: Ref<boolean>;
  isValid: ComputedRef<boolean | null>;
  isValidating: Ref<boolean>;
  validate: () => Promise<string[]>;
  reset: () => void;
  resetValidation: () => void;
  validationClasses: ComputedRef<Record<string, boolean>>;
}

export const allowedValidationOn = ['blur', 'input', 'submit'] as const;
export const makeValidationProps = propsFactory(
  {
    /**
     * Состояние ошибки
     */
    error: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Сообщения об ошибках
     */
    errorMessages: {
      type: [Array, String] as PropType<string | string[]>,
      default: () => []
    },
    /**
     * Максимально количество выводимых ошибок
     */
    maxErrors: {
      type: [Number, String] as PropType<string | number>,
      default: 1
    },
    /**
     * Имя поля
     */
    name: {
      type: String as PropType<string | undefined>,
      default: undefined
    },
    /**
     * Правила валидации
     */
    rules: {
      type: Array as PropType<TValidationRule[]>,
      default: () => []
    },
    /**
     * Значение модели
     */
    modelValue: {
      type: [String, Number, Boolean, Array, Object] as PropType<any>,
      default: null
    },
    /**
     * Событие для валидации
     */
    validateOn: {
      type: String as PropType<TValidationOn>,
      default: 'input',
      available: allowedValidationOn,
      validator: (value: TValidationOn) => allowedValidationOn.includes(value)
    },
    /**
     * Значение для валидации
     */
    validationValue: {
      type: [String, Number, Array, Object, Boolean] as PropType<any>,
      default: undefined
    },
    /**
     * Убирает слот для сообщений об ошибках
     */
    hideMessages: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  'validation'
);

export function useValidation(
  props: IValidationProps,
  name: string,
  id: MaybeRef<string | number> = getUid()
): IUseValidation {
  const model = useProxyModel(props, 'modelValue');
  const validationModel = computed(() => (props.validationValue === undefined ? model.value : props.validationValue));
  const form = useForm();
  const internalErrorMessages = ref<string[]>([]);
  const isPure = ref(true);
  const isDirty = computed(
    () =>
      !!(
        wrapInArray(model.value === '' ? null : model.value).length ||
        wrapInArray(validationModel.value === '' ? null : validationModel.value).length
      )
  );
  const isDisabled = computed(() => !!(props.disabled || form?.isDisabled.value));
  const isReadonly = computed(() => !!(props.readonly || form?.isReadonly.value));
  const errorMessages = computed(() =>
    props.errorMessages.length
      ? wrapInArray(props.errorMessages).slice(0, Math.max(0, +props.maxErrors))
      : internalErrorMessages.value
  );
  const isValid = computed(() => {
    if (props.error || errorMessages.value.length) return false;
    if (!props.rules.length) return true;

    return isPure.value ? null : true;
  });
  const isValidating = ref(false);
  const validationClasses = computed(() => ({
    [`${paramCase(name)}--error`]: isValid.value === false,
    [`${paramCase(name)}--dirty`]: isDirty.value,
    [`${paramCase(name)}--disabled`]: isDisabled.value,
    [`${paramCase(name)}--readonly`]: isReadonly.value
  }));
  const uid = computed(() => props.name ?? unref(id));

  onBeforeMount(() => {
    form?.register({
      id: uid.value,
      validate,
      reset,
      resetValidation
    });
  });

  onBeforeUnmount(() => {
    form?.unregister(uid.value);
  });

  const validateOn = computed(() => props.validateOn || form?.validateOn.value || 'input');

  onMounted(() => form?.update(uid.value, isValid.value, errorMessages.value));

  useToggleScope(
    () => validateOn.value === 'input',
    () => {
      watch(validationModel, () => {
        if (validationModel.value != null) {
          validate();
        } else if (props.focused) {
          const unwatch = watch(
            () => props.focused,
            (value) => {
              if (!value) {
                validate();
              }
              unwatch();
            }
          );
        }
      });
    }
  );

  useToggleScope(
    () => validateOn.value === 'blur',
    () => {
      watch(
        () => props.focused,
        (value) => {
          if (!value) {
            validate();
          }
        }
      );
    }
  );

  watch(isValid, () => {
    form?.update(uid.value, isValid.value, errorMessages.value);
  });

  function reset() {
    resetValidation();
    model.value = null;
  }

  function resetValidation() {
    internalErrorMessages.value = [];
    isPure.value = true;
  }

  async function validate() {
    const results = [];

    isValidating.value = true;

    for (const rule of props.rules) {
      if (results.length >= +(props.maxErrors ?? 1)) {
        break;
      }

      const handler = typeof rule === 'function' ? rule : () => rule;
      const result = await handler(validationModel.value);

      if (result === true) continue;

      if (typeof result !== 'string') {
        warn(
          'validation',
          `${result} is not a valid value. Rule functions must return boolean true or a string error message.`
        );

        continue;
      }

      results.push(result);
    }

    internalErrorMessages.value = results;
    isValidating.value = false;
    isPure.value = false;

    return internalErrorMessages.value;
  }

  return {
    errorMessages,
    isPure,
    isDirty,
    isDisabled,
    isReadonly,
    isValid,
    isValidating,
    reset,
    resetValidation,
    validate,
    validationClasses
  };
}
